import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AbilitiesService } from '../../services/abilities.service';
import { HubSurfacesService } from '../../services/hub-surfaces.service';
import { Ability, ComingSoonAbility } from '../../models/ability-catalog.model';
import { RequestAbilityDialogComponent } from '../request-ability-dialog/request-ability-dialog.component';
import { AbilityRequestDialogComponent } from '../ability-request-dialog/ability-request-dialog.component';

interface CategoryRow { category: string; icon: string; count: number; }

/** One ability matched by a search, flattened across categories so a hit is
 *  never buried one category deep (same pattern as the Agent Catalog). */
interface AbilityHit { ability: Ability; category: string; }

/** Short registry icon name → PrimeIcons glyph (unknown → neutral). */
const ICON_MAP: Record<string, string> = {
  database: 'pi-database', 'chart-line': 'pi-chart-line', chart: 'pi-chart-bar',
  'chart-pie': 'pi-chart-pie', book: 'pi-book', calendar: 'pi-calendar',
  shield: 'pi-shield', tag: 'pi-tag', file: 'pi-file', paperclip: 'pi-paperclip',
  hashtag: 'pi-hashtag', comments: 'pi-comments', chat: 'pi-comment', inbox: 'pi-inbox',
  alert: 'pi-exclamation-triangle', 'exclamation-circle': 'pi-exclamation-circle',
  users: 'pi-users', wrench: 'pi-wrench', briefcase: 'pi-briefcase',
  headphones: 'pi-headphones', mail: 'pi-envelope', envelope: 'pi-envelope',
  key: 'pi-key', box: 'pi-box', folder: 'pi-folder-open', search: 'pi-search',
  cog: 'pi-cog', bolt: 'pi-bolt', globe: 'pi-globe', send: 'pi-send',
};

/** Category → sidebar icon (same mapping the /abilities page uses). */
const CAT_ICON: Record<string, string> = {
  All: 'pi-th-large', Salesforce: 'pi-database', Commercial: 'pi-briefcase',
  People: 'pi-users', Finance: 'pi-chart-line', Licensing: 'pi-shield',
  Catsy: 'pi-tag', Knowledge: 'pi-book', Documents: 'pi-file',
  Communication: 'pi-calendar', Outreach: 'pi-send', Support: 'pi-exclamation-circle',
  Web: 'pi-globe', Operations: 'pi-cog',
};

/**
 * Abilities modal — the Studio connector abilities on the platform, in the
 * same two-pane shape as the Agent Catalog and Connections modals: categories
 * with counts in a left rail, that category's abilities as rows on the right,
 * and searching switches the right pane to matches across every category.
 *
 * The left rail also carries the admin-managed COMING SOON roadmap (the
 * abilities-roadmap entries the catalog API already returns) — previously
 * only the full /abilities page showed it, so anyone browsing from the rail
 * modal never saw what was on the way.
 *
 * Rows are pickable: the selected set hands off to Studio from the footer via
 * the SAME deep-link the /abilities page uses (HubSurfacesService
 * .openStudioPortal). The footer swaps by context — the read-only note, or
 * the selection + Build CTA — never a second stacked dialog (request access
 * goes through the existing shared dialogs, which sit above as siblings).
 * Lives in the layout shell so any surface can open it via
 * AbilitiesService.triggerOpen().
 */
@Component({
  selector: 'app-abilities-modal',
  standalone: true,
  imports: [CommonModule, AbilityRequestDialogComponent, RequestAbilityDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './abilities-modal.component.html',
  styleUrls: ['./abilities-modal.component.scss'],
})
export class AbilitiesModalComponent implements OnInit, OnDestroy {
  readonly open = signal(false);
  readonly query = signal('');
  /** Sidebar pick; null = All. The roadmap is its own view (see `view`). */
  readonly activeCategory = signal<string | null>(null);
  /** 'browse' = categories/abilities; 'soon' = the coming-soon roadmap. */
  readonly view = signal<'browse' | 'soon'>('browse');
  readonly abilities = signal<Ability[]>([]);
  readonly comingSoon = signal<ComingSoonAbility[]>([]);
  readonly categoryOrder = signal<string[]>([]);
  readonly systems = signal(0);
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly requested = signal<Set<string>>(new Set());
  /** Ids picked for the Studio handoff. Survives close/reopen within the
   *  session; cleared once handed off to Studio. */
  readonly selected = signal<Set<string>>(new Set());
  /** Ability whose "Request" is being confirmed in the shared dialog (null = closed). */
  readonly requestTarget = signal<Ability | null>(null);
  /** No-match search term being requested via app-request-ability-dialog (null = closed). */
  readonly requestTerm = signal<string | null>(null);
  /** Lowercased terms already requested this session, so the button flips to "Requested". */
  readonly searchRequested = signal<Set<string>>(new Set());
  private loaded = false;
  private readonly destroying$ = new Subject<void>();

  constructor(
    private readonly abilitiesSvc: AbilitiesService,
    private readonly surfaces: HubSurfacesService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.abilitiesSvc.openAbilities$.pipe(takeUntil(this.destroying$)).subscribe(() => {
      this.query.set('');
      this.activeCategory.set(null);
      this.view.set('browse');
      this.open.set(true);
      this.load();
      setTimeout(() => {
        document.querySelector<HTMLInputElement>('.abl-search__input')?.focus();
      }, 60);
    });
  }

  ngOnDestroy(): void { this.destroying$.next(); this.destroying$.complete(); }

  /** Fetch the catalog on open. Only a successful, non-empty result is cached
   *  for the session — a failed or empty fetch re-fetches on the next open. */
  private load(): void {
    if (this.loaded || this.loading()) return;
    this.loading.set(true);
    this.error.set(false);
    this.abilitiesSvc.getCatalog().subscribe({
      next: c => {
        this.abilities.set(c.abilities);
        this.comingSoon.set(c.comingSoon);
        this.categoryOrder.set(c.categoryOrder);
        this.systems.set(c.counts?.systems ?? 0);
        this.loading.set(false);
        this.loaded = c.abilities.length > 0;
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }

  retry(): void { this.load(); }

  close(): void { this.open.set(false); }

  /** Header "Full catalog": close and hand off to the richer /abilities page. */
  goToCatalog(): void {
    this.close();
    void this.router.navigate(['/abilities']);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    // The request dialogs handle their own Escape — don't tear the modal down too.
    if (this.requestTarget() || this.requestTerm()) return;
    if (this.open()) this.close();
  }

  // ── derived ──

  readonly total = computed(() => this.abilities().length);
  readonly trimmedQuery = computed(() => this.query().trim().toLowerCase());
  /** Typing anywhere switches the right pane to search results. */
  readonly searching = computed(() => this.trimmedQuery() !== '');

  /** Bucket key for the rail/groups — a blank/missing category must never
   *  render as a nameless row or heading (defence in depth on the service's). */
  private catOf(a: Ability): string { return (a.category ?? '').trim() || 'Other'; }

  /** Sidebar: every category in catalog order, with full (unfiltered) counts
   *  so the rail is stable while searching. */
  readonly categories = computed<CategoryRow[]>(() => {
    const counts = new Map<string, number>();
    for (const a of this.abilities()) {
      const cat = this.catOf(a);
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    }
    return this.orderCategories([...counts.keys()])
      .map(category => ({
        category,
        icon: CAT_ICON[category] ?? 'pi-folder',
        count: counts.get(category) ?? 0,
      }));
  });

  /** Abilities for the browse view (the active category, or everything —
   *  "All" keeps catalog category order so related abilities sit together). */
  readonly rows = computed<Ability[]>(() => {
    const cat = this.activeCategory();
    const rank = new Map(this.categories().map((c, i) => [c.category, i]));
    return this.abilities()
      .filter(a => !cat || this.catOf(a) === cat)
      .sort((x, y) =>
        (cat ? 0 : (rank.get(this.catOf(x)) ?? 0) - (rank.get(this.catOf(y)) ?? 0)) ||
        (x.order - y.order) || x.name.localeCompare(y.name));
  });

  /** Search view: flat hits across every category. */
  readonly hits = computed<AbilityHit[]>(() => {
    const q = this.trimmedQuery();
    if (!q) return [];
    return this.abilities()
      .filter(a => (a.name + ' ' + a.system + ' ' + a.description + ' ' + this.catOf(a))
        .toLowerCase().includes(q))
      .sort((x, y) => (x.order - y.order) || x.name.localeCompare(y.name))
      .map(ability => ({ ability, category: this.catOf(ability) }));
  });

  readonly browseTitle = computed(() => this.activeCategory() ?? 'All abilities');

  readonly empty = computed(() =>
    !this.loading() && !this.error() && this.abilities().length === 0);

  private orderCategories(present: string[]): string[] {
    const order = this.categoryOrder();
    return [
      ...order.filter(c => present.includes(c)),
      ...present.filter(c => !order.includes(c)).sort((a, b) => a.localeCompare(b)),
    ];
  }

  // ── sidebar navigation ──

  /** Searching is a different mode (flat results), so a sidebar pick drops the
   *  query — otherwise the click would look like it did nothing. */
  selectCategory(category: string | null): void {
    this.query.set('');
    this.view.set('browse');
    this.activeCategory.set(category);
    this.scrollTop();
  }

  selectSoon(): void {
    this.query.set('');
    this.view.set('soon');
    this.scrollTop();
  }

  private scrollTop(): void {
    document.querySelector('.abl-main')?.scrollTo({ top: 0 });
  }

  onSearchInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.query.set('');
    document.querySelector<HTMLInputElement>('.abl-search__input')?.focus();
  }

  // ── selection → Studio handoff ──

  readonly selectedCount = computed(() => this.selected().size);
  readonly selectedAbilities = computed<Ability[]>(() => {
    const ids = this.selected();
    return this.abilities().filter(a => ids.has(a.id));
  });

  isSelected(id: string): boolean { return this.selected().has(id); }

  /** Row body: pick an ability for the Studio handoff. Only abilities the
   *  caller already has access to can be picked — one still behind "Request
   *  access" can't be equipped in Studio yet. */
  toggleSelect(a: Ability): void {
    if (!a.hasAccess) return;
    this.selected.update(s => {
      const next = new Set(s);
      if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
      return next;
    });
  }

  clearSelection(): void { this.selected.set(new Set()); }

  /** Footer primary: open Studio deep-linked with the picked abilities — the
   *  SAME handoff the /abilities page uses — then close. */
  buildInStudio(): void {
    const abilities = this.selectedAbilities();
    if (!abilities.length) return;
    this.surfaces.openStudioPortal(abilities);
    this.selected.set(new Set());
    this.close();
  }

  // ── request access (via the shared confirmation dialogs) ──

  isRequested(id: string): boolean { return this.requested().has(id); }

  request(a: Ability): void {
    if (this.isRequested(a.id)) return;
    this.requestTarget.set(a);
  }

  closeRequestDialog(): void {
    this.requestTarget.set(null);
    this.requestTerm.set(null);
  }

  /** The dialog's POST succeeded — flip the row to its "Requested" state. */
  onRequestSent(): void {
    const a = this.requestTarget();
    if (a) this.requested.update(s => new Set(s).add(a.id));
  }

  // ── no-match "request an ability" (same flow the /abilities page offers) ──

  isSearchRequested(term: string): boolean { return this.searchRequested().has(term.trim().toLowerCase()); }

  requestSearch(): void {
    const term = this.query().trim();
    if (!term || this.isSearchRequested(term)) return;
    this.requestTerm.set(term);
  }

  onSearchRequestSubmitted(term: string): void {
    this.searchRequested.update(s => new Set(s).add(term.trim().toLowerCase()));
  }

  // ── display helpers ──

  iconFor(name: string): string {
    if (!name) return 'pi-bolt';
    if (name.startsWith('pi-')) return name;
    return ICON_MAP[name] ?? 'pi-bolt';
  }

  badgeFor(a: Ability): string { return a.write ? `Writes to ${a.system}` : `Reads ${a.system}`; }

  stop(event: Event): void { event.stopPropagation(); }
}
